/**
 * M7 S5: Tethys's leverage by career stage and the Mutual's priced refinancing (spec §9, §8.3, §4 M1 bounty).
 *
 * Every function is pure (Career in, Career out) and reads its numbers from `leverage.json` (and the income-mix
 * threshold from `logging.json`); nothing here runs per frame.
 *
 * - `careerStage` reads the stage at the moment of the offer or the retaliation (spec §9 "Detected by").
 * - `tethysOffer` prices a Tethys bribe (the M1 bounty, the Blackmail stipend's sibling). Leased: min(principalShare ×
 *   remaining principal, payments × lease payment), as cash or as waived payments; it never clears the lease (T8).
 *   Whenever the lease is still open the leased cap applies on top of the stage's own cap, so no stage's offer can
 *   buy the M2 skip.
 * - `retaliationRows` picks the rows for a stage and the income mix (every source over `dominantShare` gets its row).
 * - `applyRetaliation` lands those rows once (spec §8.3): a second path applies only the rows not yet landed and,
 *   when its multiplier is higher, only the extra hunt days. It returns the new Career plus the idempotent
 *   `setRetaliation` set and, for a newly landed hunt, one `spawnStoryShip`.
 * - `mutualRefinance` lets the Mutual buy a called debt (standing gate, premium, obligation contract);
 *   `meetMutualObligation` records a Mutual contract run; `storyDebtCycle` settles one lease due time for the
 *   story debts (collections on an unrefinanced called principal, the premium, a missed obligation's standing
 *   cost). A missed obligation never costs the ship.
 *
 * D-TBD: Tethys leverage model (S5): the stage precedence, the lease cap on every offer while leased, the leased
 * rows added at any stage while the lease is open, the hunt as a `retaliation` countdown scaled by the multiplier,
 * and refinanced called principal costing only the premium (the lease's own rate already runs).
 */
import { EPSILON, withStanding, withWallet } from '../career/ops'
import type { Career, LeaseState } from '../career/types'
import type { M2Data } from '../data/m2Schemas'
import { emptyStoryState } from './schemas'
import type { LeverageConfig, LoggingConfig } from './schemas'
import {
  INCOME_SOURCES,
  RETALIATION_ROWS,
  type CareerStage,
  type IncomeMix,
  type IncomeSource,
  type RetaliationRow,
  type StoryCommand,
  type StoryCountdown,
  type StoryDebt,
  type StoryParty,
  type StoryEffect,
  type StoryResult,
  type StoryRetaliation,
  type StoryState,
} from './types'

type LeaseData = Pick<M2Data, 'lease'>

/** The hunt's countdown id (one per career: retaliation lands once). */
export const HUNT_COUNTDOWN_ID = 'retaliation:hunt'
/** The `spawnStoryShip` key of the Asset Recovery hunt. */
export const HUNT_SHIP_KEY = 'retaliation:assetRecovery'

// ---------------------------------------------------------------------------------------------
// Stage
// ---------------------------------------------------------------------------------------------

export interface StageInput {
  leaseOwned: boolean
  /** `state.corp.faction` (the worker's `StoryWorldView.corpFaction`), null before founding. */
  corpFaction: StoryParty | null
  /** Ships financed by TCF fleet notes (spec §12.2). Counted only for a corporation. */
  fleetNotes: number
}

/** Spec §9: a corporation (with or without TCF fleet notes) outranks the pilot stages; then the lease decides. */
export function careerStage(input: StageInput): CareerStage {
  if (input.corpFaction !== null) return input.fleetNotes > 0 ? 'fleetNotes' : 'corporation'
  return input.leaseOwned ? 'owned' : 'leased'
}

// ---------------------------------------------------------------------------------------------
// Offers
// ---------------------------------------------------------------------------------------------

export interface TethysOffer {
  stage: CareerStage
  /** Credits offered (whole credits). */
  amount: number
  /** Payments that can be waived instead of cash (leased only; 0 = cash only). */
  waivablePayments: number
}

/** The leased formula: min(principalShare × remaining principal, payments × lease payment), never the whole principal. */
function leasedCap(lease: LeaseState, cfg: LeverageConfig): number {
  const o = cfg.offers.leased
  const cap = Math.min(o.principalShare * lease.principal, o.payments * lease.payment)
  return Math.max(0, Math.floor(Math.min(cap, lease.principal - 1)))
}

/** What Tethys offers now (spec §9 "Tethys offers"; §4 the M1 bounty). */
export function tethysOffer(stage: CareerStage, career: Career, cfg: LeverageConfig): TethysOffer {
  const leaseOpen = !career.lease.owned
  if (stage === 'leased' || leaseOpen) {
    const lease = leasedCap(career.lease, cfg)
    const own = stage === 'leased' ? lease : Math.min(lease, stageCashCap(stage, cfg))
    return { stage, amount: own, waivablePayments: stage === 'leased' ? waivedCount(own, career.lease) : 0 }
  }
  return { stage, amount: stageCashCap(stage, cfg), waivablePayments: 0 }
}

function stageCashCap(stage: CareerStage, cfg: LeverageConfig): number {
  if (stage === 'owned') return cfg.offers.owned.cashCap
  if (stage === 'leased') return 0
  return cfg.offers.corporation.cashCap
}

function waivedCount(amount: number, lease: LeaseState): number {
  return lease.payment > 0 ? Math.floor((amount + EPSILON) / lease.payment) : 0
}

export const OFFER_FORMS = ['cash', 'waived'] as const
export type OfferForm = (typeof OFFER_FORMS)[number]

/**
 * Takes the offer. `cash` pays the wallet. `waived` (leased only) pays the amount off the principal and moves the
 * next due time on by the payments it covers ("your next two payments waived"). Either way the lease stays open (T8).
 */
export function acceptTethysOffer(career: Career, offer: TethysOffer, form: OfferForm): Career {
  if (offer.amount <= 0) return career
  if (form === 'cash' || offer.waivablePayments <= 0 || career.lease.owned) return withWallet(career, offer.amount)
  const lease = career.lease
  const paid = Math.min(offer.amount, Math.max(0, lease.principal - 1))
  return {
    ...career,
    lease: {
      ...lease,
      principal: lease.principal - paid,
      arrears: Math.min(lease.arrears, lease.principal - paid),
      nextDueAt: lease.nextDueAt + offer.waivablePayments * lease.cycleSeconds,
    },
  }
}

// ---------------------------------------------------------------------------------------------
// Retaliation rows
// ---------------------------------------------------------------------------------------------

/** Each source's share of the window's income (0 when nothing was earned). */
export function incomeShares(mix: IncomeMix | undefined): Record<IncomeSource, number> {
  const totals: Record<IncomeSource, number> = { mining: 0, combat: 0, salvage: 0, trade: 0, contracts: 0 }
  if (!mix) return totals
  let all = 0
  for (const bucket of mix.buckets) {
    for (const src of INCOME_SOURCES) {
      totals[src] += bucket[src]
      all += bucket[src]
    }
  }
  if (all <= 0) return totals
  for (const src of INCOME_SOURCES) totals[src] /= all
  return totals
}

/** Every source over `dominantShare` of the window's income (spec §9 "every source over ~25%"). */
export function dominantSources(mix: IncomeMix | undefined, dominantShare: number): IncomeSource[] {
  const shares = incomeShares(mix)
  return INCOME_SOURCES.filter((src) => shares[src] > dominantShare)
}

const INCOME_ROWS: Readonly<Record<IncomeSource, readonly RetaliationRow[]>> = {
  mining: ['refinerySalesRefused'],
  combat: ['boardsClosed', 'interdiction'],
  salvage: ['componentsRefused', 'yardsRefused'],
  trade: ['componentsRefused', 'yardsRefused'],
  contracts: [],
}

const LEASED_ROWS: readonly RetaliationRow[] = ['principalDue', 'assetRecoveryHunt']
const OWNED_ROWS: readonly RetaliationRow[] = ['insuranceCancelled', 'dockingRefused']
const CORP_ROWS: readonly RetaliationRow[] = ['interdiction', 'claimPressure', 'sabotage']

/** Rows in `RETALIATION_ROWS` order (deterministic commands and saves). */
function ordered(rows: ReadonlySet<RetaliationRow>): RetaliationRow[] {
  return RETALIATION_ROWS.filter((r) => rows.has(r))
}

/**
 * Spec §9 "Tethys retaliates". `leaseOpen` adds the leased rows at any stage (a corporation founder still on the
 * lease has the principal called too); without it they follow the stage.
 */
export function retaliationRows(
  stage: CareerStage,
  mix: IncomeMix | undefined,
  dominantShare: number,
  leaseOpen = stage === 'leased',
): RetaliationRow[] {
  const rows = new Set<RetaliationRow>()
  if (leaseOpen || stage === 'leased') for (const r of LEASED_ROWS) rows.add(r)
  if (stage !== 'leased') {
    for (const r of OWNED_ROWS) rows.add(r)
    for (const src of dominantSources(mix, dominantShare)) for (const r of INCOME_ROWS[src]) rows.add(r)
  }
  if (stage === 'corporation' || stage === 'fleetNotes') for (const r of CORP_ROWS) rows.add(r)
  if (stage === 'fleetNotes') rows.add('fleetNotesCalled')
  return ordered(rows)
}

/** The retaliation multiplier: × `blackmailExitMult` when the path leaves Blackmail (spec §8.3). */
export function retaliationMult(blackmailExit: boolean, cfg: LeverageConfig): number {
  return blackmailExit ? cfg.retaliation.blackmailExitMult : 1
}

// ---------------------------------------------------------------------------------------------
// Applying retaliation (once)
// ---------------------------------------------------------------------------------------------

export interface RetaliationInput {
  /** `StoryWorldView.corpFaction`. */
  corpFaction: StoryParty | null
  /** Credits outstanding on TCF fleet notes (0 = none). */
  fleetNotesPrincipal: number
  /** Where the hunt starts (the player's system). */
  systemId: string
  /** `retaliationMult(...)`. */
  mult: number
}

export interface LeverageData {
  leverage: LeverageConfig
  logging: Pick<LoggingConfig, 'dominantShare'>
}

function storyOf(career: Career): StoryState {
  return career.story ?? emptyStoryState()
}

function huntDays(mult: number, cfg: LeverageConfig): number {
  return Math.ceil(cfg.retaliation.huntOnlineDays * mult)
}

/** Rows in force for the worker: every landed row, minus a hunt whose countdown has run out (or never ran). */
export function activeRetaliationRows(story: StoryState): RetaliationRow[] {
  const landed = story.retaliation?.rows ?? []
  const hunt = story.countdowns.find((c) => c.id === HUNT_COUNTDOWN_ID)
  const huntLive = hunt !== undefined && hunt.dueOnlineDay > story.onlineDays
  return landed.filter((r) => r !== 'assetRecoveryHunt' || huntLive)
}

function addDebt(debts: StoryDebt[], debt: StoryDebt): StoryDebt[] {
  if (debt.amount <= 0 || debts.some((d) => d.kind === debt.kind && d.creditor === debt.creditor)) return debts
  return [...debts, debt]
}

/**
 * Lands Tethys's retaliation once (spec §8.3, §9). Rows already landed are never re-applied; a harsher second row
 * set or a higher multiplier applies only the difference. Returns no commands when nothing changed.
 */
export function applyRetaliation(career: Career, input: RetaliationInput, data: LeverageData): StoryResult<Career> {
  const cfg = data.leverage
  const story = storyOf(career)
  const stage = careerStage({
    leaseOwned: career.lease.owned,
    corpFaction: input.corpFaction,
    fleetNotes: input.fleetNotesPrincipal > 0 ? 1 : 0,
  })
  const wanted = retaliationRows(stage, career.incomeMix, data.logging.dominantShare, !career.lease.owned)
  const prev = story.retaliation
  const landed = new Set<RetaliationRow>(prev?.rows ?? [])
  const fresh = wanted.filter((r) => !landed.has(r))
  const prevMult = prev?.mult ?? 1
  const mult = Math.max(prevMult, input.mult)
  if (fresh.length === 0 && mult <= prevMult && prev) return { career, commands: [], effects: [] }

  for (const r of fresh) landed.add(r)
  const commands: StoryCommand[] = []
  const effects: StoryEffect[] = []
  let next: Career = career
  let debts = story.debts ?? []
  let countdowns = story.countdowns
  const onlineDay = story.onlineDays

  for (const row of fresh) {
    if (row === 'principalDue') {
      debts = addDebt(debts, {
        kind: 'principalCalled',
        creditor: 'tethys',
        amount: next.lease.principal,
        cyclesLeft: cfg.retaliation.dueCycles,
      })
    } else if (row === 'fleetNotesCalled') {
      debts = addDebt(debts, {
        kind: 'fleetNotes',
        creditor: 'tethys',
        amount: input.fleetNotesPrincipal,
        cyclesLeft: cfg.retaliation.dueCycles,
      })
    } else if (row === 'insuranceCancelled' && next.lease.insured) {
      next = { ...next, lease: { ...next.lease, insured: false } }
    } else if (row === 'assetRecoveryHunt') {
      const cd: StoryCountdown = {
        id: HUNT_COUNTDOWN_ID,
        kind: 'retaliation',
        startOnlineDay: onlineDay,
        dueOnlineDay: onlineDay + huntDays(mult, cfg),
        ref: 'assetRecoveryHunt',
      }
      countdowns = [...countdowns.filter((c) => c.id !== HUNT_COUNTDOWN_ID), cd]
      commands.push({ type: 'spawnStoryShip', key: HUNT_SHIP_KEY, role: 'assetRecovery', systemId: input.systemId })
    }
  }

  // A higher multiplier on a hunt that already landed extends it by the difference only.
  if (mult > prevMult && prev?.rows.includes('assetRecoveryHunt')) {
    const extra = huntDays(mult, cfg) - huntDays(prevMult, cfg)
    countdowns = countdowns.map((c) => (c.id === HUNT_COUNTDOWN_ID ? { ...c, dueOnlineDay: c.dueOnlineDay + extra } : c))
  }

  const retaliation: StoryRetaliation = {
    rows: ordered(landed),
    mult,
    sinceOnlineDay: prev?.sinceOnlineDay ?? onlineDay,
  }
  const nextStory: StoryState = { ...story, retaliation, debts, countdowns }
  commands.unshift({ type: 'setRetaliation', faction: 'tethys', rows: activeRetaliationRows(nextStory) })
  effects.push({ type: 'autosave', reason: 'retaliation' })
  return { career: { ...next, story: nextStory }, commands, effects }
}

// ---------------------------------------------------------------------------------------------
// Mutual refinancing (spec §9 "The escape")
// ---------------------------------------------------------------------------------------------

/** The lease cycle index (one per processed due time), the clock the obligation contract runs on. */
export function leaseCycleIndex(career: Career): number {
  const lease = career.lease
  return lease.cycleSeconds > 0 ? Math.floor(lease.nextDueAt / lease.cycleSeconds) : 0
}

export type RefinanceRefusal = 'noDebt' | 'alreadyRefinanced' | 'standing'

export type RefinanceResult = { ok: true; career: Career } | { ok: false; reason: RefinanceRefusal }

/** Whether the Mutual will buy debt `index` now. */
export function canRefinance(career: Career, index: number, cfg: LeverageConfig): RefinanceRefusal | null {
  const debt = career.story?.debts?.[index]
  if (!debt || debt.amount <= 0) return 'noDebt'
  if (debt.refinanced) return 'alreadyRefinanced'
  if (career.standings.mutual < cfg.refinance.minMutualStanding) return 'standing'
  return null
}

/**
 * The Mutual buys debt `index`: no collections from now on, the premium applies every cycle and one Mutual contract
 * is due every `obligationEveryCycles` cycles until the debt is paid.
 */
export function mutualRefinance(career: Career, index: number, cfg: LeverageConfig): RefinanceResult {
  const refusal = canRefinance(career, index, cfg)
  if (refusal) return { ok: false, reason: refusal }
  const story = storyOf(career)
  const due = leaseCycleIndex(career) + cfg.refinance.obligationEveryCycles
  const debts = (story.debts ?? []).map((d, i) =>
    i === index ? { ...d, creditor: 'mutual' as const, refinanced: true, obligationDueCycle: due } : d,
  )
  return { ok: true, career: { ...career, story: { ...story, debts } } }
}

/** A Mutual contract was run: every refinanced debt's next obligation moves a full interval on from now. */
export function meetMutualObligation(career: Career, cfg: LeverageConfig): Career {
  const story = career.story
  if (!story?.debts?.some((d) => d.refinanced)) return career
  const due = leaseCycleIndex(career) + cfg.refinance.obligationEveryCycles
  const debts = story.debts.map((d) => (d.refinanced ? { ...d, obligationDueCycle: due } : d))
  return { ...career, story: { ...story, debts } }
}

export interface DebtCycleResult {
  career: Career
  effects: StoryEffect[]
  /** Debts that reached collections this cycle (unrefinanced, unpaid). */
  collected: StoryDebt['kind'][]
}

/**
 * Settles the story debts for one lease due time. Call it right after `processDue` settles that due time, so
 * `leaseCycleIndex` already names the next one.
 * - A called principal tracks the lease's principal and ends with the lease. Unrefinanced, its cycles run down and
 *   then the lease goes to `collections: 'repo'`. Refinanced, only the Mutual premium is charged (the lease's own
 *   rate keeps running).
 * - Any other debt: refinanced, it accrues the lease's standard rate plus the premium and repays up to
 *   `lease.debtPaymentPerCycle` from the wallet. Unrefinanced, it falls due after its cycles; the wallet pays what
 *   it can and the rest joins the interest-free `career.debt`.
 * - A refinanced debt whose obligation contract was missed costs Mutual standing, never the ship.
 */
export function storyDebtCycle(career: Career, cfg: LeverageConfig, leaseData: LeaseData): DebtCycleResult {
  const story = career.story
  if (!story?.debts || story.debts.length === 0) return { career, effects: [], collected: [] }
  const rate = leaseData.lease.ratePerCycle
  const cycle = leaseCycleIndex(career)
  const effects: StoryEffect[] = []
  const collected: StoryDebt['kind'][] = []
  let next: Career = career
  let missedObligation = false
  const kept: StoryDebt[] = []

  for (const debt of story.debts) {
    let d: StoryDebt = { ...debt }
    if (d.kind === 'principalCalled') {
      if (next.lease.owned) continue
      d.amount = next.lease.principal
      if (d.refinanced) {
        next = charge(next, Math.round(d.amount * cfg.refinance.premiumPerCycle))
      } else if (d.cyclesLeft > 0) {
        d.cyclesLeft -= 1
        if (d.cyclesLeft === 0 && next.lease.collections !== 'repo') {
          next = { ...next, lease: { ...next.lease, collections: 'repo' } }
          collected.push(d.kind)
          effects.push({ type: 'awayWarning', text: 'Tethys called the lease. Asset Recovery has the repossession order.' })
        }
      }
    } else if (d.refinanced) {
      d.amount += Math.round(d.amount * (rate + cfg.refinance.premiumPerCycle))
      const pay = Math.min(d.amount, leaseData.lease.debtPaymentPerCycle, Math.max(0, next.wallet))
      next = withWallet(next, -pay)
      d.amount -= pay
    } else if (d.cyclesLeft > 0) {
      d.cyclesLeft -= 1
      if (d.cyclesLeft === 0) {
        const pay = Math.min(d.amount, Math.max(0, next.wallet))
        next = { ...withWallet(next, -pay), debt: next.debt + (d.amount - pay) }
        d.amount = 0
        collected.push(d.kind)
      }
    }
    if (d.refinanced && d.obligationDueCycle !== undefined && cycle >= d.obligationDueCycle) {
      missedObligation = true
      d = { ...d, obligationDueCycle: cycle + cfg.refinance.obligationEveryCycles }
    }
    if (d.amount > EPSILON) kept.push(d)
  }

  if (missedObligation) {
    next = withStanding(next, 'mutual', cfg.refinance.missedObligationStanding)
    effects.push({ type: 'awayWarning', text: 'You missed the Mutual run you owed them. They noticed.' })
  }
  const nextStory: StoryState = { ...(next.story ?? story), debts: kept }
  return { career: { ...next, story: nextStory }, effects, collected }
}

/** Charges the wallet; a shortfall joins the interest-free `career.debt`. */
function charge(career: Career, amount: number): Career {
  if (amount <= 0) return career
  const pay = Math.min(amount, Math.max(0, career.wallet))
  const after = withWallet(career, -pay)
  return amount - pay > EPSILON ? { ...after, debt: after.debt + (amount - pay) } : after
}
